import { useEffect, useState, useRef } from "react";
import Skeleton from "react-loading-skeleton";
import Prestation from "./prestation";

const ServicesRdv = ({ services, status, buttonAction }) => {
  const [selectedService, setSelectedService] = useState(null);
  const sliderRef = useRef(null);
  const [sliderPosition, setSliderPosition] = useState(0);
  const [maxPosition, setMaxPosition] = useState(0);

  // Initialize default selected service and max position
  useEffect(() => {
    if (services && services.length > 0) {
      setSelectedService(services[0]);
      calculateMaxPosition();
    }
  }, [services]);

  // Calculate max scroll position
  const calculateMaxPosition = () => {
    if (sliderRef.current) {
      const containerWidth = sliderRef.current.offsetWidth;
      const contentWidth = sliderRef.current.scrollWidth;
      setMaxPosition(contentWidth - containerWidth);
    }
  };

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      calculateMaxPosition();
      // Adjust position if needed after resize
      if (sliderRef.current) {
        sliderRef.current.scrollLeft = sliderPosition;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sliderPosition]);

  // Loading state
  if (status === "loading") {
    return (
      <div className="etablissement-services mt-24">
        <div className="single row">
          <div className="services-col col-md-8">
            <h3 className="grey500">
              <Skeleton width={200} />
            </h3>
            <div className="service-cards-carousel">
              {[...Array(5)].map((_, index) => (
                <div key={index} className="service-card-carousel">
                  <Skeleton width={150} height={35} />
                </div>
              ))}
            </div>
          </div>
          <div className="hours-col col-md-4">
            <Skeleton width="100%" height={300} />
            <Skeleton width="100%" height={300} style={{ marginTop: "8px" }} />
          </div>
        </div>
      </div>
    );
  }

  if (!services || services.length === 0) {
    return <div>No services available.</div>;
  }

  const handleScroll = (direction) => {
    if (!sliderRef.current) return;

    const containerWidth = sliderRef.current.offsetWidth;
    const scrollAmount = containerWidth * 0.8; // Scroll 80% of container width

    let newPosition;
    if (direction === 'next') {
      newPosition = Math.min(sliderPosition + scrollAmount, maxPosition);
    } else {
      newPosition = Math.max(sliderPosition - scrollAmount, 0);
    }

    sliderRef.current.scrollTo({
      left: newPosition,
      behavior: 'smooth'
    });
    setSliderPosition(newPosition);
  };

  const handleServiceSelect = (service) => {
    setSelectedService(service);
  };

  return (
    <div className="etablissement-services mt-24">
      <div className="single row">
        <div className="services-col">
          <h3 className="grey500">Prestations</h3>

          {/* Carousel Container */}
          <div className="service-carousel-container d-flex align-items-center gap-2">
            {/* Services Carousel */}
            <div
              className="service-cards-carousel"
              ref={sliderRef}
              style={{
                display: "flex",
                gap: "12px",
                overflowX: "auto",
                padding: "6px 4px",
                scrollBehavior: "smooth",
                WebkitOverflowScrolling: "touch",
                alignItems: "center",
                flex: 1,
              }}
              onScroll={(e) => setSliderPosition(e.target.scrollLeft)}
            >
              {services.map((service) => (
                <div
                  key={service.id}
                  className={`service-card-carousel ${
                    selectedService?.id === service.id ? "active" : ""
                  }`}
                  onClick={() => handleServiceSelect(service)}
                  style={{
                    flex: "0 0 auto",
                    padding: "8px 18px",
                    borderRadius: "9999px",
                    whiteSpace: "nowrap",
                    border:
                      selectedService?.id === service.id
                        ? "none"
                        : "1px solid #e5e7eb",
                    background:
                      selectedService?.id === service.id ? "#2563eb" : "#fff",
                    color:
                      selectedService?.id === service.id ? "#fff" : "#374151",
                    fontWeight: 600,
                    cursor: "pointer",
                    position: "relative",
                    transition: "all 0.2s ease",
                  }}
                >
                  {service.name}
                </div>
              ))}
            </div>

            {/* Left Arrow */}
            <button
              onClick={() => handleScroll("prev")}
              className="carousel-arrow link-blue"
              disabled={sliderPosition <= 0}
              aria-label="Services précédents"
            >
              <img src="/images/left.svg" alt="Previous" />
            </button>

            {/* Right Arrow */}
            <button
              onClick={() => handleScroll("next")}
              className="carousel-arrow link-blue"
              disabled={sliderPosition >= maxPosition}
              aria-label="Services suivants"
            >
              <img
                src="/images/left.svg"
                alt="Next"
                style={{ transform: "rotate(180deg)" }}
              />
            </button>
          </div>

          {selectedService && (
            <div className="prestations">
              {selectedService.prestations && selectedService.prestations.length === 0 ? (
                <p className="body grey66">
                  Aucune prestation disponible pour ce service.
                </p>
              ) : (
                selectedService.prestations.map((prestation, index) => (
                  <Prestation
                    key={index}
                    prestation={prestation}
                    selected={false}
                    classNameBtn="twyny-cta-secondary"
                    buttonAction={() => buttonAction(prestation)}
                    buttonName="Choisir"
                  />
                ))
              )}
            </div>
          )}

          {/* CSS for carousel */}
          <style>{`
            .service-cards-carousel::-webkit-scrollbar { display: none; }
            .service-cards-carousel.is-dragging { cursor: grabbing; user-select: none; }
            .service-card-carousel:hover {
              transform: translateY(-1px);
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
          `}</style>
        </div>
        <div className="hours-col col-md-4"></div>
      </div>
    </div>
  );
};

export default ServicesRdv;